// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { transformError } from "@transformers/response";
import {
  GetReferralList,
  GetReferral,
  GetReferralSchema,
  AddReferral,
  StartDraftReferral,
  DuplicateReferral,
  UpdateReferral,
  UpdateReferralAssignments,
  DisableReferral,
  DiscardReferralDraft,
  SubmitReferralForReview,
  CancelReferralReview,
  CancelReferralReviewAndDiscard,
} from "@modules/service/constants/actions";

import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { Referral } from "@modules/service/reducer/referrals/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type ReferralListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetReferralList({
  payload,
}: Action<ReferralListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<Referral>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process`,
    );

    yield put(actionCreators.GetReferralListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetReferralListError(transformError(e)));
  }
}

type ReferralRequest = ReferralListRequest &
  $Exact<{
    processId: string,
  }>;

export function* onGetReferral({
  payload,
}: Action<ReferralRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/assignment`,
    );

    yield all([
      put(actionCreators.GetReferralSuccess(stableResponse.data)),
      put(actionCreators.SetReferralAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetReferralError(transformError(e)));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(actionCreators.GetReferralList({ clientId: params.clientId }));

  if (params.processId) {
    yield put(
      actionCreators.GetReferral({
        clientId: params.clientId,
        processId: params.processId,
      }),
    );
  }
}

// CREATE

export function* onGetReferralSchema({
  payload,
}: Action<ReferralListRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const schema = yield parseSchema("ReferralProcessRevisionDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetReferralSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetReferralSchemaError(transformError(e)));
  }
}

export function* onStartDraftReferral({
  payload,
}: Action<ReferralRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}`,
    );

    yield all([
      put(actionCreators.StartDraftReferralSuccess(response.data)),
      put(actionCreators.SetShowEditReferralModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftReferralError(transformError(e)));
  }
}

export function* onDuplicateReferral({
  payload,
}: Action<ReferralRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateReferralSuccess(response.data)),
      put(actionCreators.SetReferralAssignments([])),
      put(actionCreators.SetShowAddReferralModal(true)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateReferralError(transformError(e)));
  }
}

type ReferralAddRequest = ReferralListRequest &
  $Exact<{
    draft: Referral,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddReferral({ payload }: Action<ReferralAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${response.data.id}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(sendSuccessMessage("DRAFT_CREATED")),
      put(actionCreators.AddReferralSuccess(response.data)),
      put(actionCreators.SetShowAddReferralModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.AddReferralError(transformError(e.response)));
  }
}

// UPDATE

type ReferralUpdateRequest = ReferralRequest &
  $Exact<{
    draft: Referral,
  }>;

export function* onUpdateReferral({
  payload,
}: Action<ReferralUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(sendSuccessMessage("DRAFT_UPDATED")),
      put(actionCreators.UpdateReferralSuccess(response.data)),
      put(actionCreators.SetShowEditReferralModal(false)),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReferralError(transformError(e.response)));
  }
}

type ReferralUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<Referral>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateReferralAssignments({
  payload,
}: Action<ReferralUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/referral/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });

    yield put(actionCreators.UpdateReferralAssignmentsSuccess());
    yield put(actionCreators.SetShowEditReferralAssignmentsModal(false));
  } catch (e) {
    yield put(actionCreators.UpdateReferralAssignmentsError(transformError(e)));
  }
}

// DISABLE

export type ReferralDisableRequest = ReferralRequest &
  $Exact<{
    approvable: ApprovableContainer<Referral>,
  }>;

export function* onDisable({
  payload,
}: Action<ReferralDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<Referral>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/draft`,
      {
        ...draft,
        isActive: !draft.isActive,
      },
    );

    yield call(http.post, payload.clientId, payload.processId, {
      notes: "Referral disabled and resubmitted for review",
    });

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.DisableReferralSuccess(response.data)),
      put(actionCreators.SetShowDisableReferralModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);

    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableReferralError(transformError(e)));
  }
}

// STATE TRANSITIONS

export type ReferralTransitionRequest = $Exact<{
  clientId: string,
  processId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<ReferralTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.DiscardReferralDraftSuccess()),
      put(actionCreators.SetShowDiscardReferralModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardReferralDraftError(transformError(e)));
  }
}

export type ReferralSubmitForReviewRequest = $Exact<{
  ...ReferralTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<ReferralSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.SubmitReferralForReviewSuccess()),
      put(actionCreators.SetShowSubmitReferralModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitReferralForReviewError(transformError(e)));
  }
}

export function* onCancelReview({
  payload,
}: Action<ReferralTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/cancel-review`,
    );
    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.CancelReferralReviewSuccess()),
      put(actionCreators.SetShowCancelReferralModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelReferralReviewError(transformError(e)));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<ReferralTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process/${payload.processId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId, processId: payload.processId });

    yield all([
      put(actionCreators.CancelReferralReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardReferralModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_PROCESSES", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelReferralReviewError(transformError(e)));
  }
}

// ROOT

export function* ReferralSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetReferralList, onGetReferralList),
    takeEvery(GetReferral, onGetReferral),
    takeEvery(GetReferralSchema, onGetReferralSchema),
    takeEvery(AddReferral, onAddReferral),
    takeEvery(StartDraftReferral, onStartDraftReferral),
    takeEvery(DuplicateReferral, onDuplicateReferral),
    takeEvery(UpdateReferral, onUpdateReferral),
    takeEvery(UpdateReferralAssignments, onUpdateReferralAssignments),
    takeEvery(DisableReferral, onDisable),
    takeEvery(DiscardReferralDraft, onDiscardDraft),
    takeEvery(SubmitReferralForReview, onSubmitForReview),
    takeEvery(CancelReferralReview, onCancelReview),
    takeEvery(CancelReferralReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default ReferralSagas;
